/**
 * Chrome every ViewSpec template shares: title, the agent's claim (caption), the chart itself,
 * any callout annotations, and the basis string in small print. The title and caption always
 * render — see `ViewChrome` — since the caption is the one-sentence claim the view exists to
 * support; callouts, the basis line and the `note` slot are `chrome`-gated.
 *
 * Reference lines are NOT rendered here — each template draws its own `RefLine` annotations,
 * because only the template knows its coordinate transform. This shell owns everything that
 * doesn't need one.
 */

import type { ReactNode } from "react";

import type { Callout, RefLine } from "@/lib/api";
import { cn } from "@/lib/utils";

export type ViewSize = "card" | "expanded";

/**
 * How a template's chart slot is sized.
 *
 * `"intrinsic"` (the default) is for the templates that lay themselves out in HTML — bar rows,
 * histogram bars, table rows. The slot takes their content height wherever nothing constrains
 * it (the chat rail, the popup), so a ten-row bar chart is ten rows tall rather than spilling
 * out over the card below it.
 *
 * `"measured"` is for the SVG templates, which draw at the box's own pixel size (see
 * `useChartSize`) and therefore need a DEFINITE height to measure — `basis` supplies one where
 * nothing else does, while `flex-1 min-h-0` still lets a fixed-height pin card grow or shrink
 * it. Their content can never exceed the slot, because their content is measured FROM it.
 */
export type ChartSizing = "intrinsic" | "measured";

/**
 * The fields every template's generated type shares — hand-written rather than `Pick`-ed off a
 * named `ViewSpec` union, because `openapi-ts` never emits one (each template's `annotations` /
 * `basis` are duplicated inline on `FieldView`, `BarView`, …, not factored into a shared type).
 */
interface SharedSpecFields {
  title: string;
  caption: string;
  basis?: string | null;
  annotations?: Array<({ kind: "refline" } & RefLine) | ({ kind: "callout" } & Callout)>;
}

/** `"full"` (the default) shows every piece of chrome a template has: the caption, callouts,
 *  the basis line, and whatever supplementary note the template itself prints below its chart.
 *  `"bare"` shows only the title, the one-sentence caption — the claim the view exists to
 *  support — and the chart itself; everything else moves into a click-to-open popup instead
 *  (see `ViewCard`), since it's reference material, not the point. */
export type ViewChrome = "bare" | "full";

interface ViewShellProps {
  spec: SharedSpecFields;
  size: ViewSize;
  children: ReactNode;
  /** Extra content between the caption and the chart, e.g. a "static data" notice. */
  note?: ReactNode;
  chrome?: ViewChrome;
  /** See `ChartSizing` — `"measured"` for the SVG templates, intrinsic for the HTML ones. */
  chartSizing?: ChartSizing;
}

function isCallout(
  a: ({ kind: "refline" } & RefLine) | ({ kind: "callout" } & Callout),
): a is { kind: "callout" } & Callout {
  return a.kind === "callout";
}

export function ViewShell({
  spec,
  size,
  children,
  note,
  chrome = "full",
  chartSizing = "intrinsic",
}: ViewShellProps) {
  const callouts = (spec.annotations ?? []).filter(isCallout);
  const bare = chrome === "bare";
  return (
    <div className={cn("flex h-full min-h-0 flex-col gap-2", size === "expanded" && "gap-3")}>
      <div>
        <h3
          className={cn(
            "font-heading text-foreground font-semibold",
            size === "card" ? "text-sm" : "text-lg",
          )}
        >
          {spec.title}
        </h3>
        <p
          className={cn("text-muted mt-0.5 leading-snug", size === "card" ? "text-xs" : "text-sm")}
        >
          {spec.caption}
        </p>
      </div>
      {bare ? null : note}
      {/* Sized per `chartSizing` — a fixed basis for the SVG templates that measure their own
          box, content height for the HTML ones (a ten-row bar chart is ten rows tall, not a
          200px box with six rows hanging out of it). */}
      <div
        className={cn(
          "min-h-0 min-w-0 flex-1",
          chartSizing === "measured" && (size === "card" ? "basis-[200px]" : "basis-[320px]"),
        )}
      >
        {children}
      </div>
      {!bare && callouts.length > 0 ? (
        <ul className="space-y-1">
          {callouts.map((c, i) => (
            <li key={i} className="text-muted-foreground text-xs italic">
              {c.text}
            </li>
          ))}
        </ul>
      ) : null}
      {!bare && spec.basis ? (
        <p className="text-muted-foreground text-[11px]">{spec.basis}</p>
      ) : null}
    </div>
  );
}
